import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Settings } from 'lucide-react'
import { toast } from '@/hooks/use-toast'
import { useUpdateWorkflow } from '@/features/admin/workflowsQueries'
import { WORKFLOW_TYPES, type AdminWorkflow } from '@/types/admin'
import type { ApiRequestError } from '@/services/api'

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
})

type FormValues = z.infer<typeof schema>

interface EditWorkflowDialogProps {
  organizationId: string
  workflow: AdminWorkflow | null
  onOpenChange: (open: boolean) => void
}

export function EditWorkflowDialog({ organizationId, workflow, onOpenChange }: EditWorkflowDialogProps) {
  const navigate = useNavigate()
  const updateWorkflow = useUpdateWorkflow(organizationId)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: workflow?.name ?? '' },
  })

  useEffect(() => {
    reset({ name: workflow?.name ?? '' })
  }, [workflow, reset])

  function close() {
    onOpenChange(false)
  }

  function openSetup() {
    if (!workflow) return
    close()
    navigate(`/admin/organizations/${organizationId}/workflows/${workflow.id}/setup`)
  }

  const onSubmit = handleSubmit(async (values) => {
    if (!workflow) return
    try {
      await updateWorkflow.mutateAsync({ workflowId: workflow.id, name: values.name })
      toast({ title: 'Workflow updated' })
      close()
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not update workflow',
      })
    }
  })

  const typeLabel = WORKFLOW_TYPES.find((t) => t.value === workflow?.type)?.label ?? workflow?.type

  return (
    <Dialog open={!!workflow} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename workflow</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="edit-workflow-name">
              Name
            </label>
            <Input id="edit-workflow-name" {...register('name')} />
            {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Type</label>
            <Input value={typeLabel ?? ''} disabled />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Audience</label>
            <Input value={workflow?.audience === 'customer' ? 'Customer' : 'Test'} disabled />
          </div>
          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="outline" onClick={openSetup}>
              <Settings className="h-4 w-4" />
              Setup
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateWorkflow.isPending}>
                {updateWorkflow.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
